import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Bell, Menu, LogOut, UserCog, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const TopBar = ({ onMenu }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const ref = useRef(null);
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);
  const [theme, toggle] = useTheme();

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <header className="print:hidden sticky top-0 z-20 h-[72px] flex items-center gap-3 px-4 sm:px-6 lg:px-8 border-b border-slate-200/70 dark:border-white/5 bg-white/70 dark:bg-[#050C22]/70 backdrop-blur-xl">
      <button className="lg:hidden text-slate-600 dark:text-slate-300" onClick={onMenu} data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></button>
      <form onSubmit={(e) => { e.preventDefault(); nav(`/records?q=${encodeURIComponent(q.trim())}`); }} className="flex-1 max-w-xl relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input" placeholder="Search reference no., title, sender, payee…"
          className="w-full h-11 pl-10 pr-16 rounded-full text-sm outline-none transition-[background-color,border-color,box-shadow] bg-slate-100/80 border border-transparent focus:bg-white focus:border-blue-300 dark:bg-white/5 dark:text-slate-100 dark:focus:bg-white/10 dark:focus:border-sky-400/40" />
        <kbd className="hidden sm:block absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-400 border border-slate-300 dark:border-white/10 rounded px-1.5 py-0.5">Ctrl K</kbd>
      </form>
      <div className="ml-auto flex items-center gap-1.5">
        <button onClick={toggle} data-testid="theme-toggle-button" title={theme === "dark" ? "Switch to Light Executive" : "Switch to Dark Executive"}
          className="w-10 h-10 grid place-items-center rounded-full transition-colors hover:bg-slate-100 dark:hover:bg-white/10 text-slate-600 dark:text-amber-200">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        <Link to="/notifications" className="relative w-10 h-10 grid place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition-colors" data-testid="header-notifications-bell">
          <Bell className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          {unread > 0 && <span data-testid="unread-count" className="pulse-gold absolute top-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full bg-gradient-to-br from-amber-300 to-amber-600 text-[#1a1203] text-[10px] font-bold grid place-items-center px-1">{unread}</span>}
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors" data-testid="user-menu-trigger">
            <Avatar className="w-9 h-9 ring-2 ring-amber-400/60"><AvatarImage src={user.picture} /><AvatarFallback>{user.name?.[0]}</AvatarFallback></Avatar>
            <div className="hidden md:block text-left leading-tight">
              <div className="text-sm font-semibold text-slate-800">{user.name}</div>
              <div className="text-[11px] text-amber-700 dark:text-amber-200/80" data-testid="current-user-role">{user.role_label}</div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white w-56">
            <DropdownMenuLabel className="text-xs text-slate-500 font-normal">{user.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><UserCog className="w-4 h-4 mr-2" />Notification settings</DropdownMenuItem>
            <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
